import type { Severity, SystemProvider, SystemStatus } from '../../api/types'

type Tone = 'ok' | 'warn' | 'danger' | 'muted' | 'info' | 'gold'

/** Tinted micro-labels readable on white cards. */
const tones: Record<Tone, string> = {
  ok: 'border-ostrel-ok/40 bg-ostrel-ok/10 text-ostrel-ok',
  warn: 'border-ostrel-warn/50 bg-ostrel-warn/10 text-ostrel-warn',
  danger: 'border-ostrel-danger/40 bg-ostrel-danger/10 text-ostrel-danger',
  muted: 'border-ostrel-line bg-ostrel-sand text-ostrel-ink-muted',
  info: 'border-ostrel-info/40 bg-ostrel-info/10 text-ostrel-info',
  gold: 'border-ostrel-gold/50 bg-ostrel-gold/10 text-ostrel-gold',
}

export interface ChipProps {
  tone?: Tone
  label: string
  className?: string
  /** Native tooltip — for a badge whose label alone doesn't explain what to
   *  do about it. */
  title?: string
}

/** Uppercase micro-label state chip. */
export function Chip({ tone = 'muted', label, className = '', title }: ChipProps) {
  return (
    <span
      title={title}
      className={`tnum inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tones[tone]} ${className}`}
    >
      {label}
    </span>
  )
}

/** Inheritance-barrier marker for tree/picker rows: a node with
 *  `inherits_grants === false` needs a DIRECT assignment — rules/grants at
 *  ancestors don't cover it. */
export function GrantBarrierMark() {
  return (
    <span
      role="img"
      aria-label="Requires direct assignment — grants above don't inherit in"
      title="Requires direct assignment — grants above don't inherit in"
      className="shrink-0 text-[10px]"
    >
      🔒
    </span>
  )
}

const statusMap: Record<SystemStatus, { tone: Tone; label: string }> = {
  active: { tone: 'ok', label: 'Active' },
  needs_sites: { tone: 'warn', label: 'Needs sites' },
  auth_failed: { tone: 'danger', label: 'Auth-failed' },
  draft: { tone: 'muted', label: 'Draft' },
  duplicate: { tone: 'warn', label: 'Duplicate' },
  disabled: { tone: 'muted', label: 'Disabled' },
}

export function StatusChip({ status }: { status: SystemStatus }) {
  const { tone, label } = statusMap[status] ?? { tone: 'muted' as Tone, label: status }
  return <Chip tone={tone} label={label} />
}

/** Colored status dot (sidebar system list, operator presence). */
const dotMap: Record<SystemStatus, string> = {
  active: 'bg-ostrel-ok',
  needs_sites: 'bg-ostrel-warn',
  duplicate: 'bg-ostrel-warn',
  auth_failed: 'bg-ostrel-danger',
  draft: 'bg-ostrel-dim',
  disabled: 'bg-ostrel-dim',
}

export function StatusDot({ status, className = '' }: { status: SystemStatus; className?: string }) {
  return (
    <span
      className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${dotMap[status] ?? 'bg-ostrel-dim'} ${className}`}
    />
  )
}

const severityMap: Record<Severity, Tone> = {
  info: 'info',
  notice: 'muted',
  warning: 'warn',
  critical: 'danger',
}

export function SeverityChip({ severity }: { severity: Severity }) {
  return <Chip tone={severityMap[severity] ?? 'muted'} label={severity} />
}

const armStateMap: Record<string, { tone: Tone; label: string }> = {
  armed: { tone: 'ok', label: 'Armed' },
  armed_away: { tone: 'ok', label: 'Armed' },
  armed_home: { tone: 'ok', label: 'Armed home' },
  partial: { tone: 'warn', label: 'Part armed' },
  disarmed: { tone: 'muted', label: 'Disarmed' },
  triggered: { tone: 'danger', label: 'Triggered' },
  unknown: { tone: 'muted', label: 'Unknown' },
}

export function ArmStateChip({ state }: { state: string }) {
  const { tone, label } = armStateMap[state] ?? { tone: 'muted' as Tone, label: state }
  return <Chip tone={tone} label={label} />
}

const providerTags: Record<SystemProvider, string> = {
  axema: 'VAKA',
  techfass: 'TFAS',
  acre: 'ACRE',
  came: 'BPT',
  wisenet: 'WAVE',
}

export function providerTag(provider: SystemProvider): string {
  return providerTags[provider] ?? provider.toUpperCase()
}

/** Bordered mono provider tag (VAKA / TFAS / ACRE / BPT / WAVE). */
export function ProviderTag({ provider }: { provider: SystemProvider }) {
  return (
    <span className="tnum inline-flex items-center rounded-sm border border-ostrel-line bg-ostrel-sand px-1 py-0.5 font-mono text-[9px] uppercase tracking-wider text-ostrel-ink-muted">
      {providerTag(provider)}
    </span>
  )
}
